import React, { useEffect, useMemo, useState } from "react";
import Table from "../../components/Table";
import { Column } from "react-table";
import BarChart from "../../components/chartTypes/Barchart";
import {
  generateDrawdownAnalysisSummary,
  generateOrginationSummary,
} from "../../services/orginationSummary";
import MultiSelectCheckboxGroup from "../../components/CheckBoxFilter";
import Loader from "../../components/Loader";
import { baseURL } from "../../services";
import { useTranslation } from "react-i18next";
import {
  sixMonthsAgoFormatted,
  twoDaysAgoFormatted,
} from "../../constants/intex";
import { listBranches } from "../../services/filterApis";
import Table2 from "../../components/Table2";
import Table3 from "../../components/Table3";

// Updated Branch colors for comparative mode
const BRANCH_COLORS = [
  {
    bgColor: "#E3F2FD", // Light Blue background
    textColor: "#0D47A1", // Dark Blue text
    borderColor: "#90CAF9", // Blue border
  },
  {
    bgColor: "#E8F5E9", // Light Green background
    textColor: "#1B5E20", // Dark Green text
    borderColor: "#A5D6A7", // Green border
  },
  {
    bgColor: "#FFF3E0", // Light Orange background
    textColor: "#E65100", // Dark Orange text
    borderColor: "#FFCC80", // Orange border
  },
];

type BranchColor = {
  bgColor: string;
  textColor: string;
  borderColor: string;
};

interface OrginationSummary {
  month: string;
  period: string;
  start_date: string;
  end_date: string;
  total_loans: string;
  emi_loans: string;
  ewi_loans: string;
  emi_loans_percentage: string;
}

interface OrginationRs {
  period: string;
  total_loan_amount: string;
  emi_loan_amount: string;
  ewi_loan_amount: string;
}

const columns: Column<OrginationSummary>[] = [
  { Header: "Period", accessor: "period" },
  { Header: "#Loans", accessor: "total_loans" },
  { Header: "EMI Loans", accessor: "emi_loans" },
  { Header: "EWI Loans", accessor: "ewi_loans" },
  { Header: "EMI Loans %", accessor: "emi_loans_percentage" },
];

const disbursedColumns: Column<OrginationRs>[] = [
  { Header: "Period", accessor: "period" },
  { Header: "Total(₹)", accessor: "total_loan_amount" },
  { Header: "EMI(₹)", accessor: "emi_loan_amount" },
  { Header: "EWI(₹)", accessor: "ewi_loan_amount" },
];

const OriginationSummary = () => {
  const { t } = useTranslation();

  const [orginationSummary, setOrginationSummary] = useState<any[]>([]);
  const [drawdownAnalysisSummary, setDrawdownAnalysisSummary] = useState<any[]>(
    []
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [drawdownAnalysisSummaryLoading, setDrawdownAnalysisSummaryLoading] =
    useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isValid, setIsValid] = React.useState(true);
  const [isDownloadDisabled, setIsDownloadDisabled] = React.useState(false);
  const [branchColors, setBranchColors] = useState<Record<string, BranchColor>>(
    {}
  );

  const [pendingFilters, setPendingFilters] = useState<
    Record<string, string[]>
  >(() => ({
    period: ["month"],
    fromDate: [sixMonthsAgoFormatted],
    toDate: [twoDaysAgoFormatted],
    displaymode: ["1"],
  }));

  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string[]>
  >(() => ({
    period: ["month"],
    fromDate: [sixMonthsAgoFormatted],
    toDate: [twoDaysAgoFormatted],
    displaymode: ["1"],
  }));
  const [applyFilter, setApplyFilter] = useState(0);

  const filters = [
    { key: "state", type: "select", label: t("filters.state") },
    { key: "cluster", type: "select", label: t("filters.cluster") },
    {
      key: "displaymode",
      type: "singleSelect",
      label: t("filters.displaymode"),
    },
    { key: "branch", type: "select", label: t("filters.branch") },
    { key: "period", type: "singleSelect", label: t("filters.period") },
    {
      key: "fromDate",
      type: "date",
      label: "From Date",
      maxValue: selectedFilters.toDate,
    },
    {
      key: "toDate",
      type: "date",
      label: "To Date",
      maxValue: twoDaysAgoFormatted,
    },
  ];

  const [branchList, setBranchList] = useState<
    { branch_id: string; branch_name: string }[]
  >([]);
  const [comparativeLoading, setComparativeLoading] = useState<boolean>(false);
  const [comparativeTables, setComparativeTables] = useState<any[][]>([]);
  const [comparativeDrawdownLoading, setComparativeDrawdownLoading] =
    useState(false);
  const [comparativeDrawdownSummary, setComparativeDrawdownSummary] = useState<
    any[][]
  >([]);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await listBranches(selectedFilters.state || []);
        const branches = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
          ? response
          : [];
        setBranchList(branches);
      } catch (error) {
        console.error("Error loading branches", error);
        setBranchList([]);
      }
    };

    fetchBranches();
  }, [selectedFilters.state]);

  const branchIdToNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (Array.isArray(branchList)) {
      branchList.forEach((branch) => {
        const id = branch?.branch_id;
        const name = branch?.branch_name;
        if (id && name) {
          map[id] = name;
        }
      });
    }
    return map;
  }, [branchList]);

  const getBranchName = (branchId: string): string => {
    if (branchIdToNameMap[branchId]) return branchIdToNameMap[branchId];
    const branch = branchList.find((b) => b.branch_id === branchId);
    if (branch) return branch.branch_name || branchId;
    return branchId;
  };

  const getGenerateOrginationSummary = async () => {
    const displaymodeRaw = selectedFilters.displaymode?.[0] || "1";
    const displaymode = displaymodeRaw.trim().toLowerCase();
    const isComparative = displaymode === "comparative" || displaymode === "2";

    setLoading(true);

    try {
      if (isComparative) {
        setComparativeLoading(true);
        const selectedBranches = selectedFilters.branch || [];

        // Assign colors to branches
        const colors: Record<string, BranchColor> = {};
        selectedBranches.forEach((branchId, index) => {
          colors[branchId] = BRANCH_COLORS[index % BRANCH_COLORS.length];
        });
        setBranchColors(colors);

        const branchPromises = selectedBranches.map((branchId) =>
          generateOrginationSummary({
            ...selectedFilters,
            branch: [branchId],
          })
        );

        const branchResponses = await Promise.all(branchPromises);
        const comparativeData = branchResponses.map((res) =>
          Array.isArray(res.data) ? res.data : res.data?.summary || []
        );

        setComparativeTables(comparativeData);

        // Fetch orginationSummary for period keys
        const periodResponse = await generateOrginationSummary({
          ...selectedFilters,
          branch: [], // remove branch to get all-periods view
        });

        const periodData = Array.isArray(periodResponse.data)
          ? periodResponse.data
          : periodResponse.data?.summary || [];

        setOrginationSummary(periodData);
      } else {
        const response = await generateOrginationSummary(selectedFilters);
        setOrginationSummary(
          Array.isArray(response.data)
            ? response.data
            : response.data?.summary || []
        );
      }
    } catch (err) {
      setError("Failed to fetch data");
      console.error(err);
    } finally {
      setLoading(false);
      setComparativeLoading(false);
    }
  };

  const getDrawdownAnalysisSummary = async () => {
    const displaymodeRaw = selectedFilters.displaymode?.[0] || "consolidate";
    const displaymode = displaymodeRaw.trim().toLowerCase();
    const isComparative = displaymode === "comparative" || displaymode === "2";

    setDrawdownAnalysisSummaryLoading(true);

    try {
      if (isComparative) {
        setComparativeDrawdownLoading(true);
        const selectedBranches = selectedFilters.branch || [];

        const branchPromises = selectedBranches.map((branchId) =>
          generateDrawdownAnalysisSummary({
            ...selectedFilters,
            branch: [branchId],
          })
        );

        const branchResponses = await Promise.all(branchPromises);
        setComparativeDrawdownSummary(branchResponses.map((res) => res.data));
      } else {
        const response = await generateDrawdownAnalysisSummary(selectedFilters);
        setDrawdownAnalysisSummary(response.data);
      }
    } catch (err) {
      setError("Failed to fetch drawdown analysis");
      console.error(err);
    } finally {
      setDrawdownAnalysisSummaryLoading(false);
      setComparativeDrawdownLoading(false);
    }
  };

  const handleApplyClick = () => {
    const displaymodeRaw = pendingFilters.displaymode?.[0] || "consolidate";
    const displaymode = displaymodeRaw.trim().toLowerCase();
    const selectedBranches = pendingFilters.branch || [];
    const isComparative = displaymode === "comparative" || displaymode === "2";

    if (isComparative) {
      if (selectedBranches.length < 2 || selectedBranches.length > 3) {
        setValidationError(
          "Please select minimum two and maximum three branches for comparative mode."
        );
        setIsValid(false);
        setIsDownloadDisabled(true);
        return;
      }
    }

    setSelectedFilters(pendingFilters);
    setValidationError("");
    setIsValid(true);
    setIsDownloadDisabled(false);
    setApplyFilter((prev) => prev + 1);
  };

  const handleFilterChange = (updatedValues: Record<string, string[]>) => {
    setPendingFilters(updatedValues);
  };

  const handleCancelClick = () => {
    setSelectedFilters({
      period: ["month"],
      fromDate: [sixMonthsAgoFormatted],
      toDate: [twoDaysAgoFormatted],
      displaymode: ["1"],
    });
    setApplyFilter(0);
    setPendingFilters({
      period: ["month"],
      fromDate: [sixMonthsAgoFormatted],
      toDate: [twoDaysAgoFormatted],
      displaymode: ["1"],
    });
  };

  const handleDownloadClick = () => {
    const link = `${baseURL}/generate/csv/new_origination_summary/list?from_date=${
      selectedFilters.fromDate ?? ""
    }&to_date=${selectedFilters.toDate ?? ""}&group_by=${
      selectedFilters.period ?? ""
    }&state_id=${selectedFilters.state ?? ""}&territory_id=${
      selectedFilters.territory ?? ""
    }&cluster_id=${selectedFilters.cluster ?? ""}&branch_id=${
      selectedFilters.branch ?? ""
    }`;
    window.location.href = link;
  };

  const keyLabelingData: any = {
    emi_loan_details: "EMI",
    ewi_loan_details: "EWI",
    new_to_kpb: "New-to-KPB",
    existing_to_kpb: "Existing-to-KPB",
    past_kpb: "Past-KPB",
    percentage_disb_amount: "% Disb Amt",
    percentage_loans: "% Loans",
  };

  useEffect(() => {
    getGenerateOrginationSummary();
    getDrawdownAnalysisSummary();
  }, [applyFilter]);

  const periodMap: Record<string, string> = {
    w: t("newOriginationSummary.w"),
    m: t("newOriginationSummary.m"),
    q: t("newOriginationSummary.q"),
    d: t("newOriginationSummary.d"),
  };

  const allPeriods =
    comparativeDrawdownSummary[0]?.map((item: any) => item.period) || [];

  const formatCurrency = (amount: number) => {
    if (amount >= 10000000) {
      return `${(amount / 10000000).toFixed(2)} Cr`;
    }
    if (amount >= 100000) {
      return `${(amount / 100000).toFixed(2)} L`;
    }
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div>
      {/* Page Content */}
      <div className="page-content">
        {/* Dynamic Grey Header */}
        <div className="header">{t("newOriginationSummary.pageTitle")}</div>

        {/* filter section */}
        <div className="content px-3">
          <MultiSelectCheckboxGroup
            filters={filters}
            selectedValues={pendingFilters}
            onChange={handleFilterChange}
            isLoading={[
              loading,
              drawdownAnalysisSummaryLoading,
              comparativeLoading,
              comparativeDrawdownLoading,
            ]}
            buttons={[
              { label: t("buttons.apply"), onClick: () => handleApplyClick() },
              {
                label: t("buttons.cancel"),
                onClick: () => handleCancelClick(),
              },
              {
                label: t("buttons.download"),
                onClick: () => handleDownloadClick(),
                disabled: !isValid,
              },
            ]}
          />
          {validationError && (
            <div
              style={{
                color: "#B00020",
                backgroundColor: "#FDECEA",
                padding: "12px 16px",
                border: "1px solid #F5C6CB",
                borderRadius: "6px",
                fontWeight: "500",
                marginTop: "10px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
              }}
            >
              ⚠️ {validationError}
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="content px-1">
          {/* New Orgination # section */}
          <div className="p-2">
            {!validationError && (
              <>
                {applyFilter &&
                selectedFilters.displaymode?.[0]?.trim().toLowerCase() ===
                  "2" ? (
                  <div style={{ width: "100%", overflow: "auto" }}>
                    {(() => {
                      // Create combined data structure
                      const branchDataMap = new Map<string, any[]>();
                      (selectedFilters.branch || []).forEach(
                        (branchId, index) => {
                          branchDataMap.set(
                            branchId,
                            comparativeTables?.[index] || []
                          );
                        }
                      );

                      // Create combined data using period match
                      const combinedData = (orginationSummary || []).map(
                        (periodData) => {
                          const row: any = { period: periodData.period };

                          (selectedFilters.branch || []).forEach((branchId) => {
                            const branchData =
                              branchDataMap.get(branchId) || [];
                            const matchingPeriodData =
                              branchData.find(
                                (d: any) => d.period === periodData.period
                              ) || {};

                            row[`branch_${branchId}`] = {
                              totalLoans: matchingPeriodData.total_loans || 0,
                              emiLoans: matchingPeriodData.emi_loans || 0,
                              ewiLoans: matchingPeriodData.ewi_loans || 0,
                              emiLoansPercent:
                                matchingPeriodData.emi_loans_percentage || 0,
                            };
                          });

                          return row;
                        }
                      );

                      return (
                        <Table2
                          columns={[
                            // Period column
                            {
                              Header: "Period",
                              accessor: "period",
                              sticky: "left",
                              style: {
                                fontWeight: "bold",
                                borderRight: "1px solid #000",
                              },
                            },
                            // #Loans group
                            {
                              Header: "#Loans",
                              headerStyle: {
                                borderBottom: "1px solid #000",
                                borderRight: "1px solid #000",
                              },
                              columns:
                                selectedFilters.branch?.map((branchId) => ({
                                  Header: getBranchName(branchId),
                                  accessor: `branch_${branchId}.totalLoans`,
                                  Cell: ({ value }: { value?: number }) =>
                                    value ?? "-",
                                  style: {
                                    backgroundColor:
                                      branchColors[branchId]?.bgColor ||
                                      "#f5f5f5",
                                    borderRight: "1px solid #ddd",
                                    borderLeft: `3px solid ${
                                      branchColors[branchId]?.borderColor ||
                                      "#ddd"
                                    }`,
                                  },
                                })) || [],
                            },
                            // EMI Loans group
                            {
                              Header: "EMI Loans",
                              headerStyle: {
                                borderBottom: "1px solid #000",
                                borderRight: "1px solid #000",
                              },
                              columns:
                                selectedFilters.branch?.map((branchId) => ({
                                  Header: getBranchName(branchId),
                                  accessor: `branch_${branchId}.emiLoans`,
                                  Cell: ({ value }: { value?: number }) =>
                                    value ?? "-",
                                  style: {
                                    backgroundColor:
                                      branchColors[branchId]?.bgColor ||
                                      "#f9f9f9",
                                    borderRight: "1px solid #ddd",
                                    borderLeft: `3px solid ${
                                      branchColors[branchId]?.borderColor ||
                                      "#ddd"
                                    }`,
                                  },
                                })) || [],
                            },
                            // EWI Loans group
                            {
                              Header: "EWI Loans",
                              headerStyle: {
                                borderBottom: "1px solid #000",
                                borderRight: "1px solid #000",
                              },
                              columns:
                                selectedFilters.branch?.map((branchId) => ({
                                  Header: getBranchName(branchId),
                                  accessor: `branch_${branchId}.ewiLoans`,
                                  Cell: ({ value }: { value?: number }) =>
                                    value ?? "-",
                                  style: {
                                    backgroundColor:
                                      branchColors[branchId]?.bgColor ||
                                      "#f5f5f5",
                                    borderRight: "1px solid #ddd",
                                    borderLeft: `3px solid ${
                                      branchColors[branchId]?.borderColor ||
                                      "#ddd"
                                    }`,
                                  },
                                })) || [],
                            },
                            // EMI Loans % group
                            {
                              Header: "EMI Loans %",
                              headerStyle: {
                                borderBottom: "1px solid #000",
                              },
                              columns:
                                selectedFilters.branch?.map((branchId) => ({
                                  Header: getBranchName(branchId),
                                  accessor: `branch_${branchId}.emiLoansPercent`,
                                  Cell: ({ value }: { value?: number }) => {
                                    if (value === undefined || value === null)
                                      return "-";
                                    return `${value.toFixed(1)}`;
                                  },
                                  style: {
                                    backgroundColor:
                                      branchColors[branchId]?.bgColor ||
                                      "#f9f9f9",
                                    borderRight: "1px solid #ddd",
                                    borderLeft: `3px solid ${
                                      branchColors[branchId]?.borderColor ||
                                      "#ddd"
                                    }`,
                                  },
                                })) || [],
                            },
                          ]}
                          data={combinedData}
                          tableHead={`${t("newOriginationSummary.tableHead1")}`}
                          type="1"
                          loading={comparativeLoading || loading}
                          isComparative={true}
                          branchCount={selectedFilters.branch?.length || 1}
                          branchColors={branchColors}
                          branchIds={selectedFilters.branch || []}
                        />
                      );
                    })()}
                  </div>
                ) : (
                  <div style={{ width: "100%", overflow: "scroll" }}>
                    <Table
                      columns={columns}
                      data={orginationSummary}
                      tableHead={t("newOriginationSummary.tableHead1")}
                      type="1"
                      loading={loading}
                    />
                  </div>
                )}
              </>
            )}
          </div>
          <div className="p-2 mt-5">
            {!validationError && (
              <>
                {applyFilter &&
                selectedFilters.displaymode?.[0]?.trim().toLowerCase() ===
                  "2" ? (
                  <div style={{ width: "100%", overflow: "auto" }}>
                    {(() => {
                      // Create combined data structure
                      const branchDataMap = new Map<string, any[]>();
                      (selectedFilters.branch || []).forEach(
                        (branchId, index) => {
                          branchDataMap.set(
                            branchId,
                            comparativeTables?.[index] || []
                          );
                        }
                      );

                      // Create combined data using period match
                      const combinedData = (orginationSummary || []).map(
                        (periodData) => {
                          const row: any = { period: periodData.period };

                          (selectedFilters.branch || []).forEach((branchId) => {
                            const branchData =
                              branchDataMap.get(branchId) || [];
                            const matchingPeriodData =
                              branchData.find(
                                (d: any) => d.period === periodData.period
                              ) || {};

                            row[`branch_${branchId}`] = {
                              totalAmount:
                                matchingPeriodData.total_loan_amount || "-",
                              emiAmount:
                                matchingPeriodData.emi_loan_amount || "-",
                              ewiAmount:
                                matchingPeriodData.ewi_loan_amount || "-",
                            };
                          });

                          return row;
                        }
                      );

                      return (
                        <Table3
                          columns={[
                            // Period column
                            {
                              Header: "Period",
                              accessor: "period",
                              sticky: "left",
                              style: {
                                fontWeight: "bold",
                                borderRight: "2px solid #000",
                                backgroundColor: "#ffffff",
                              },
                            },
                            // Total(₹) group
                            {
                              Header: "Total(₹)",
                              headerStyle: {
                                borderBottom: "2px solid #000",
                                borderRight: "2px solid #000",
                                backgroundColor: "#f5f5f5",
                              },
                              columns:
                                selectedFilters.branch?.map((branchId) => ({
                                  Header: getBranchName(branchId),
                                  accessor: `branch_${branchId}.totalAmount`,
                                  Cell: ({ value }: { value?: string }) =>
                                    value || "-",
                                  style: {
                                    backgroundColor:
                                      branchColors[branchId]?.bgColor ||
                                      "#f5f5f5",
                                    borderRight: "1px solid #ddd",
                                    borderLeft: `3px solid ${
                                      branchColors[branchId]?.borderColor ||
                                      "#ddd"
                                    }`,
                                  },
                                })) || [],
                            },
                            // EMI(₹) group
                            {
                              Header: "EMI(₹)",
                              headerStyle: {
                                borderBottom: "2px solid #000",
                                borderRight: "2px solid #000",
                                backgroundColor: "#f9f9f9",
                              },
                              columns:
                                selectedFilters.branch?.map((branchId) => ({
                                  Header: getBranchName(branchId),
                                  accessor: `branch_${branchId}.emiAmount`,
                                  Cell: ({ value }: { value?: string }) =>
                                    value || "-",
                                  style: {
                                    backgroundColor:
                                      branchColors[branchId]?.bgColor ||
                                      "#f9f9f9",
                                    borderRight: "1px solid #ddd",
                                    borderLeft: `3px solid ${
                                      branchColors[branchId]?.borderColor ||
                                      "#ddd"
                                    }`,
                                  },
                                })) || [],
                            },
                            // EWI(₹) group
                            {
                              Header: "EWI(₹)",
                              headerStyle: {
                                borderBottom: "2px solid #000",
                                backgroundColor: "#f5f5f5",
                              },
                              columns:
                                selectedFilters.branch?.map((branchId) => ({
                                  Header: getBranchName(branchId),
                                  accessor: `branch_${branchId}.ewiAmount`,
                                  Cell: ({ value }: { value?: string }) =>
                                    value || "-",
                                  style: {
                                    backgroundColor:
                                      branchColors[branchId]?.bgColor ||
                                      "#f5f5f5",
                                    borderRight: "1px solid #ddd",
                                    borderLeft: `3px solid ${
                                      branchColors[branchId]?.borderColor ||
                                      "#ddd"
                                    }`,
                                  },
                                })) || [],
                            },
                          ]}
                          data={combinedData}
                          tableHead={`${t("newOriginationSummary.tableHead2")}`}
                          type="1"
                          loading={comparativeLoading || loading}
                          isComparative={true}
                          branchCount={selectedFilters.branch?.length || 1}
                          branchColors={branchColors}
                          branchIds={selectedFilters.branch || []}
                        />
                      );
                    })()}
                  </div>
                ) : (
                  <div style={{ width: "100%", overflow: "scroll" }}>
                    <Table
                      columns={[
                        {
                          Header: "Period",
                          accessor: "period",
                          sticky: "left",
                          style: { fontWeight: "bold" },
                        },
                        {
                          Header: "Total(₹)",
                          accessor: "total_loan_amount",
                          Cell: ({ value }: { value?: string }) => value || "-",
                          Footer: () => {
                            const total = (orginationSummary || []).reduce(
                              (sum, row) => {
                                const amount =
                                  parseFloat(
                                    row.total_loan_amount?.replace(" Cr", "") ||
                                      "0"
                                  ) * 10000000 ||
                                  parseFloat(
                                    row.total_loan_amount?.replace(" L", "") ||
                                      "0"
                                  ) * 100000;
                                return sum + amount;
                              },
                              0
                            );
                            return `Total: ${formatCurrency(total)}`;
                          },
                        },
                        {
                          Header: "EMI(₹)",
                          accessor: "emi_loan_amount",
                          Cell: ({ value }: { value?: string }) => value || "-",
                          Footer: () => {
                            const total = (orginationSummary || []).reduce(
                              (sum, row) => {
                                const amount =
                                  parseFloat(
                                    row.emi_loan_amount?.replace(" Cr", "") ||
                                      "0"
                                  ) * 10000000 ||
                                  parseFloat(
                                    row.emi_loan_amount?.replace(" L", "") ||
                                      "0"
                                  ) * 100000;
                                return sum + amount;
                              },
                              0
                            );
                            return `Total: ${formatCurrency(total)}`;
                          },
                        },
                        {
                          Header: "EWI(₹)",
                          accessor: "ewi_loan_amount",
                          Cell: ({ value }: { value?: string }) => value || "-",
                          Footer: () => {
                            const total = (orginationSummary || []).reduce(
                              (sum, row) => {
                                const amount =
                                  parseFloat(
                                    row.ewi_loan_amount?.replace(" Cr", "") ||
                                      "0"
                                  ) * 10000000 ||
                                  parseFloat(
                                    row.ewi_loan_amount?.replace(" L", "") ||
                                      "0"
                                  ) * 100000;
                                return sum + amount;
                              },
                              0
                            );
                            return `Total: ${formatCurrency(total)}`;
                          },
                        },
                      ]}
                      data={orginationSummary}
                      tableHead={t("newOriginationSummary.tableHead2")}
                      type="1"
                      loading={loading}
                    />
                  </div>
                )}
              </>
            )}
          </div>
          {/* Draw down analysis section */}
          <div className="p-2">
            {!validationError && (
              <>
                {applyFilter &&
                selectedFilters.displaymode?.[0]?.trim().toLowerCase() ===
                  "2" ? (
                  <div
                    className="mt-5"
                    style={{ width: "100%", overflow: "scroll" }}
                  >
                    {comparativeDrawdownLoading ? (
                      <Loader />
                    ) : (
                      <div>
                        <h1
                          style={{
                            fontSize: 15,
                            fontWeight: 600,
                            fontFamily: "Arial",
                            fontStyle: "normal",
                            textAlign: "start",
                            paddingLeft: "15px",
                            color: "#14213d",
                          }}
                        >
                          {t(
                            "newOriginationSummary.Drawdown Analysis (NTB vs. ETB).pageTitle"
                          )}
                        </h1>

                        <table className="table" style={{ margin: "20px" }}>
                          <thead>
                            <tr style={{ fontSize: 12, fontWeight: 300 }}>
                              <th
                                className="tableHead"
                                rowSpan={2}
                                style={{ minWidth: "130px" }}
                              >
                                {t(
                                  "newOriginationSummary.Drawdown Analysis (NTB vs. ETB).Payment Type"
                                )}
                              </th>
                              <th
                                className="tableHead"
                                rowSpan={2}
                                style={{ minWidth: "130px" }}
                              >
                                {t(
                                  "newOriginationSummary.Drawdown Analysis (NTB vs. ETB).Releation Type"
                                )}
                              </th>
                              <th
                                className="tableHead"
                                rowSpan={2}
                                style={{ minWidth: "100px" }}
                              ></th>
                              {comparativeDrawdownSummary[0]?.map((obj) => (
                                <th
                                  className="tableHead"
                                  colSpan={selectedFilters.branch?.length || 0}
                                  style={{
                                    textAlign: "center",
                                    borderLeft: "2px solid #999",
                                  }}
                                  key={obj.period}
                                >
                                  {obj.period}
                                </th>
                              ))}
                            </tr>
                            <tr style={{ fontSize: 12, fontWeight: 300 }}>
                              {comparativeDrawdownSummary[0]?.map(
                                (obj) =>
                                  selectedFilters.branch?.map(
                                    (branchId, branchIndex) => (
                                      <th
                                        className="tableHead"
                                        key={`${obj.period}-${branchId}`}
                                        style={{
                                          borderRight: "1px solid #999",
                                          borderLeft:
                                            branchIndex === 0
                                              ? "1px solid #999"
                                              : "none",
                                          backgroundColor:
                                            branchColors[branchId]?.bgColor ||
                                            "#f5f5f5",
                                          color:
                                            branchColors[branchId]?.textColor ||
                                            "#000",
                                        }}
                                      >
                                        {getBranchName(branchId)}
                                      </th>
                                    )
                                  ) || []
                              )}
                            </tr>
                          </thead>
                          <tbody style={{ fontSize: 13, fontWeight: 400 }}>
                            {["emi_loan_details", "ewi_loan_details"].map(
                              (paymentType) => (
                                <React.Fragment key={paymentType}>
                                  {[
                                    "new_to_kpb",
                                    "existing_to_kpb",
                                    "past_kpb",
                                  ].map((category, catIndex) => {
                                    const isLabelRow = true;
                                    return (
                                      <React.Fragment
                                        key={`${paymentType}-${category}`}
                                      >
                                        {/* % Disb Amt Row */}
                                        <tr
                                          style={{
                                            borderTop: "1px solid #999",
                                          }}
                                        >
                                          {catIndex === 0 && (
                                            <td
                                              rowSpan={6}
                                              className="border text-center align-middle"
                                              style={{
                                                minWidth: "120px",
                                                border: "1px solid #999",
                                              }}
                                            >
                                              {keyLabelingData[paymentType]}
                                            </td>
                                          )}
                                          <td
                                            rowSpan={2}
                                            className="border text-center align-middle"
                                            style={{
                                              minWidth: "100px",
                                              backgroundColor: isLabelRow
                                                ? "#e9ecef"
                                                : "white",
                                              border: "1px solid #999",
                                            }}
                                          >
                                            {keyLabelingData[category]}
                                          </td>
                                          <td
                                            className="border"
                                            style={{
                                              minWidth: "100px",
                                              backgroundColor: isLabelRow
                                                ? "#e9ecef"
                                                : "white",
                                              border: "1px solid #999",
                                            }}
                                          >
                                            % Disb Amt
                                          </td>
                                          {comparativeDrawdownSummary[0]?.map(
                                            (periodObj) =>
                                              selectedFilters.branch?.map(
                                                (branchId, branchIndex) => {
                                                  const branchData =
                                                    comparativeDrawdownSummary[
                                                      branchIndex
                                                    ] || [];
                                                  const periodData =
                                                    branchData.find(
                                                      (d) =>
                                                        d.period ===
                                                        periodObj.period
                                                    );
                                                  const color =
                                                    branchColors[branchId] ||
                                                    {};
                                                  return (
                                                    <td
                                                      key={`${branchId}-${periodObj.period}-disb`}
                                                      style={{
                                                        minWidth: "100px",
                                                        backgroundColor:
                                                          color.bgColor ||
                                                          "#ffffff",
                                                        color:
                                                          color.textColor ||
                                                          "#000",
                                                        borderLeft:
                                                          branchIndex === 0
                                                            ? `2px solid ${
                                                                color.borderColor ||
                                                                "#999"
                                                              }`
                                                            : "none",
                                                        borderRight:
                                                          branchIndex ===
                                                          (selectedFilters
                                                            .branch?.length ||
                                                            0) -
                                                            1
                                                            ? `2px solid ${
                                                                color.borderColor ||
                                                                "#999"
                                                              }`
                                                            : "1px solid #999",
                                                        borderTop:
                                                          "1px solid #999",
                                                        borderBottom:
                                                          "1px solid #999",
                                                      }}
                                                    >
                                                      {periodData?.[
                                                        paymentType
                                                      ]?.[category]
                                                        ?.percentage_disb_amount ??
                                                        "--"}{" "}
                                                      %
                                                    </td>
                                                  );
                                                }
                                              )
                                          )}
                                        </tr>

                                        {/* % Loans Row */}
                                        <tr
                                          style={{
                                            borderBottom: "1px solid #999",
                                          }}
                                        >
                                          <td
                                            className="border"
                                            style={{
                                              minWidth: "100px",
                                              backgroundColor: isLabelRow
                                                ? "#e9ecef"
                                                : "white",
                                              border: "1px solid #999",
                                            }}
                                          >
                                            % Loans
                                          </td>
                                          {comparativeDrawdownSummary[0]?.map(
                                            (periodObj) =>
                                              selectedFilters.branch?.map(
                                                (branchId, branchIndex) => {
                                                  const branchData =
                                                    comparativeDrawdownSummary[
                                                      branchIndex
                                                    ] || [];
                                                  const periodData =
                                                    branchData.find(
                                                      (d) =>
                                                        d.period ===
                                                        periodObj.period
                                                    );
                                                  const color =
                                                    branchColors[branchId] ||
                                                    {};
                                                  return (
                                                    <td
                                                      key={`${branchId}-${periodObj.period}-loans`}
                                                      style={{
                                                        minWidth: "100px",
                                                        backgroundColor:
                                                          color.bgColor ||
                                                          "#ffffff",
                                                        color:
                                                          color.textColor ||
                                                          "#000",
                                                        borderLeft:
                                                          branchIndex === 0
                                                            ? `2px solid ${
                                                                color.borderColor ||
                                                                "#999"
                                                              }`
                                                            : "none",
                                                        borderRight:
                                                          branchIndex ===
                                                          (selectedFilters
                                                            .branch?.length ||
                                                            0) -
                                                            1
                                                            ? `2px solid ${
                                                                color.borderColor ||
                                                                "#999"
                                                              }`
                                                            : "1px solid #999",
                                                        borderTop:
                                                          "1px solid #999",
                                                        borderBottom:
                                                          "1px solid #999",
                                                      }}
                                                    >
                                                      {periodData?.[
                                                        paymentType
                                                      ]?.[category]
                                                        ?.percentage_loans ??
                                                        "--"}{" "}
                                                      %
                                                    </td>
                                                  );
                                                }
                                              )
                                          )}
                                        </tr>
                                      </React.Fragment>
                                    );
                                  })}
                                </React.Fragment>
                              )
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ) : (
                  <div
                    className="mt-5"
                    style={{ width: "100%", overflow: "scroll" }}
                  >
                    <h1
                      style={{
                        fontSize: 15,
                        fontWeight: 600,
                        fontFamily: "Arial",
                        fontStyle: "normal",
                        textAlign: "start",
                        paddingLeft: "15px",
                        color: "#14213d",
                      }}
                    >
                      {t(
                        "newOriginationSummary.Drawdown Analysis (NTB vs. ETB).pageTitle"
                      )}
                    </h1>

                    {drawdownAnalysisSummaryLoading ? (
                      <div
                        className="d-flex justify-content-center align-items-center"
                        style={{ minHeight: "50vh" }}
                      >
                        <Loader />
                      </div>
                    ) : drawdownAnalysisSummary.length > 0 ? (
                      <table className="table" style={{ margin: "20px" }}>
                        <thead>
                          <tr style={{ fontSize: 12, fontWeight: 300 }}>
                            <th
                              className="tableHead"
                              style={{ minWidth: "130px" }}
                            >
                              {t(
                                "newOriginationSummary.Drawdown Analysis (NTB vs. ETB).Payment Type"
                              )}
                            </th>
                            <th
                              className="tableHead"
                              style={{ minWidth: "130px" }}
                            >
                              {t(
                                "newOriginationSummary.Drawdown Analysis (NTB vs. ETB).Releation Type"
                              )}
                            </th>
                            <th className="tableHead"></th>
                            {drawdownAnalysisSummary.map((obj) => (
                              <th className="tableHead" key={obj.period}>
                                {obj.period}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody style={{ fontSize: 13, fontWeight: 400 }}>
                          {["emi_loan_details", "ewi_loan_details"].map(
                            (paymentType, typeIndex) =>
                              ["new_to_kpb", "existing_to_kpb", "past_kpb"].map(
                                (category, catIndex) =>
                                  [
                                    "percentage_disb_amount",
                                    "percentage_loans",
                                  ].map((key, rowIndex) => {
                                    const isGray = [
                                      "new_to_kpb",
                                      "past_kpb",
                                    ].includes(category);

                                    return (
                                      <tr
                                        key={`${typeIndex}-${catIndex}-${rowIndex}`}
                                      >
                                        {rowIndex === 0 && catIndex === 0 && (
                                          <td
                                            rowSpan={6}
                                            className="border text-center align-middle"
                                            style={{
                                              minWidth: "120px",
                                            }}
                                          >
                                            {keyLabelingData[paymentType]}
                                          </td>
                                        )}
                                        {rowIndex === 0 && (
                                          <td
                                            rowSpan={2}
                                            className="border text-center align-middle"
                                            style={{
                                              minWidth: "100px",
                                              backgroundColor: isGray
                                                ? "#e9ecef"
                                                : "white",
                                            }}
                                          >
                                            {keyLabelingData[category]}
                                          </td>
                                        )}
                                        <td
                                          className="border"
                                          style={{
                                            minWidth: "100px",
                                            backgroundColor: isGray
                                              ? "#e9ecef"
                                              : "white",
                                          }}
                                        >
                                          {keyLabelingData[key]}
                                        </td>
                                        {drawdownAnalysisSummary.map((obj) => (
                                          <td
                                            key={`${catIndex}-${rowIndex}-${obj.period}`}
                                            style={{
                                              minWidth: "100px",
                                              backgroundColor: isGray
                                                ? "#e9ecef"
                                                : "white",
                                            }}
                                          >
                                            {obj?.[paymentType]?.[category]?.[
                                              key
                                            ] ?? "--"}{" "}
                                            %
                                          </td>
                                        ))}
                                      </tr>
                                    );
                                  })
                              )
                          )}
                        </tbody>
                      </table>
                    ) : (
                      <p className="px-3">
                        No data available for the selected criteria
                      </p>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          {/* Ticket size by ntb/etb section */}
          <div className="container-fluid">
            <div className="row">
              <div className="col-12">
                {!validationError && (
                  <>
                    {applyFilter &&
                    selectedFilters.displaymode?.[0]?.trim().toLowerCase() ===
                      "2" ? (
                      <div className="mt-5 p-1">
                        {/* Table Section - Full width */}
                        <div style={{ width: "100%", overflowX: "auto" }}>
                          <h1
                            style={{
                              fontSize: 15,
                              fontWeight: 600,
                              fontFamily: "Arial",
                              fontStyle: "normal",
                              textAlign: "start",
                              paddingLeft: "15px",
                              color: "#14213d",
                            }}
                          >
                            {t(
                              "newOriginationSummary.Ticket Size by (NTB / ETB).pageTitle"
                            )}
                          </h1>

                          {comparativeDrawdownLoading ? (
                            <Loader />
                          ) : comparativeDrawdownSummary[0]?.length > 0 ? (
                            <div style={{ overflowX: "auto" }}>
                              <table
                                className="table"
                                style={{ margin: "20px", minWidth: "800px" }}
                              >
                                <thead>
                                  <tr style={{ fontSize: 12, fontWeight: 300 }}>
                                    <th
                                      className="tableHead"
                                      rowSpan={2}
                                      style={{ minWidth: "130px" }}
                                      scope="col"
                                    >
                                      {t(
                                        "newOriginationSummary.Ticket Size by (NTB / ETB).Payment Type"
                                      )}
                                    </th>
                                    <th
                                      className="tableHead"
                                      rowSpan={2}
                                      style={{ minWidth: "130px" }}
                                      scope="col"
                                    >
                                      {t(
                                        "newOriginationSummary.Ticket Size by (NTB / ETB).Releation Type"
                                      )}
                                    </th>
                                    {allPeriods.map((period: string) => (
                                      <th
                                        className="tableHead"
                                        key={period}
                                        scope="col"
                                        colSpan={
                                          selectedFilters.branch?.length || 1
                                        }
                                        style={{
                                          borderLeft: "2px solid #ddd",
                                          textAlign: "center",
                                        }}
                                      >
                                        {period}
                                      </th>
                                    ))}
                                  </tr>
                                  <tr style={{ fontSize: 12, fontWeight: 300 }}>
                                    {allPeriods.map((period: string) =>
                                      selectedFilters.branch?.map(
                                        (
                                          branchId: string,
                                          branchIndex: number
                                        ) => (
                                          <th
                                            className="tableHead"
                                            key={`${period}-${branchId}`}
                                            scope="col"
                                            style={{
                                              minWidth: "100px",
                                              borderRight:
                                                branchIndex ===
                                                (selectedFilters.branch
                                                  ?.length || 0) -
                                                  1
                                                  ? "2px solid #ddd"
                                                  : "1px solid #ddd",
                                              borderLeft:
                                                branchIndex === 0
                                                  ? "2px solid #ddd"
                                                  : "none",
                                              backgroundColor:
                                                branchColors[branchId]
                                                  ?.bgColor || "#f5f5f5",
                                              color:
                                                branchColors[branchId]
                                                  ?.textColor || "#000",
                                            }}
                                          >
                                            {getBranchName(branchId)}
                                          </th>
                                        )
                                      )
                                    )}
                                  </tr>
                                </thead>
                                <tbody
                                  style={{ fontSize: 13, fontWeight: 400 }}
                                >
                                  {["emi_loan_details", "ewi_loan_details"].map(
                                    (paymentType, typeIndex) =>
                                      [
                                        "new_to_kpb",
                                        "existing_to_kpb",
                                        "past_kpb",
                                      ].map((category, catIndex) => (
                                        <tr key={`${paymentType}-${category}`}>
                                          {catIndex === 0 && (
                                            <td
                                              rowSpan={3}
                                              className="border text-center align-middle"
                                              style={{
                                                minWidth: "120px",
                                                border: "1px solid #999",
                                              }}
                                            >
                                              {keyLabelingData[paymentType]}
                                            </td>
                                          )}
                                          <td
                                            className="border text-center align-middle"
                                            style={{
                                              minWidth: "100px",
                                              backgroundColor: "#e9ecef",
                                              border: "1px solid #999",
                                            }}
                                          >
                                            {keyLabelingData[category]}
                                          </td>
                                          {allPeriods.map((period) =>
                                            selectedFilters.branch?.map(
                                              (branchId, branchIndex) => {
                                                const branchData =
                                                  comparativeDrawdownSummary[
                                                    branchIndex
                                                  ] || [];
                                                const periodData =
                                                  branchData.find(
                                                    (d) => d.period === period
                                                  );
                                                const color =
                                                  branchColors[branchId] || {};
                                                const value =
                                                  periodData?.[paymentType]?.[
                                                    category
                                                  ]?.avg_loan_disb_amt;

                                                return (
                                                  <td
                                                    key={`${paymentType}-${category}-${period}-${branchId}`}
                                                    style={{
                                                      minWidth: "100px",
                                                      backgroundColor:
                                                        color.bgColor ||
                                                        "#ffffff",
                                                      color:
                                                        color.textColor ||
                                                        "#000",
                                                      borderLeft:
                                                        branchIndex === 0
                                                          ? `2px solid ${
                                                              color.borderColor ||
                                                              "#ddd"
                                                            }`
                                                          : "none",
                                                      borderRight:
                                                        branchIndex ===
                                                        (selectedFilters.branch
                                                          ?.length || 0) -
                                                          1
                                                          ? `2px solid ${
                                                              color.borderColor ||
                                                              "#ddd"
                                                            }`
                                                          : "1px solid #ddd",
                                                      borderTop:
                                                        "1px solid #999",
                                                      borderBottom:
                                                        "1px solid #999",
                                                    }}
                                                  >
                                                    {value ?? "--"}
                                                  </td>
                                                );
                                              }
                                            )
                                          )}
                                        </tr>
                                      ))
                                  )}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p>No data available</p>
                          )}
                        </div>

                        {/* Combined Chart Section */}
                        <div className="mt-5 p-3" style={{ width: "100%" }}>
                          <h1
                            style={{
                              fontSize: 15,
                              fontWeight: 600,
                              fontFamily: "Arial",
                              fontStyle: "normal",
                              textAlign: "start",
                              paddingLeft: "15px",
                              color: "#14213d",
                            }}
                          >
                            {t("newOriginationSummary.title")}
                          </h1>
                          <div style={{ width: "100%" }}>
                            <BarChart
                              title=""
                              labels={allPeriods}
                              dataSets={
                                selectedFilters.branch?.flatMap(
                                  (branchId: string, index: number) => {
                                    const branchName = getBranchName(branchId);
                                    const branchData =
                                      comparativeTables?.[index] || [];
                                    const color =
                                      BRANCH_COLORS[
                                        index % BRANCH_COLORS.length
                                      ];

                                    const emiDataPoints = allPeriods.map(
                                      (period) => {
                                        const periodData = branchData.find(
                                          (d) => d.period === period
                                        );
                                        return periodData?.ticket_size_emi ?? 0;
                                      }
                                    );

                                    const ewiDataPoints = allPeriods.map(
                                      (period) => {
                                        const periodData = branchData.find(
                                          (d) => d.period === period
                                        );
                                        return periodData?.ticket_size_ewi ?? 0;
                                      }
                                    );

                                    return [
                                      {
                                        label: `${branchName} - ${t(
                                          "newOriginationSummary.label1"
                                        )}`,
                                        data: emiDataPoints,
                                        backgroundColor: color.borderColor,
                                      },
                                      {
                                        label: `${branchName} - ${t(
                                          "newOriginationSummary.label2"
                                        )}`,
                                        data: ewiDataPoints,
                                        backgroundColor: color.borderColor,
                                        borderColor: color.borderColor,
                                        borderWidth: 1,
                                      },
                                    ];
                                  }
                                ) || []
                              }
                              xLabel={
                                periodMap[selectedFilters?.period?.[0]] ||
                                "Period"
                              }
                              yLabel={t("newOriginationSummary.yLabel")}
                            />
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Consolidated Mode (unchanged) */
                      <div className="row mt-5 p-1">
                        {/* Table Section */}
                        <div
                          className="col-md-7"
                          style={{ overflow: "scroll", height: "45vh" }}
                        >
                          <h1
                            style={{
                              fontSize: 15,
                              fontWeight: 600,
                              fontFamily: "Arial",
                              fontStyle: "normal",
                              textAlign: "start",
                              paddingLeft: "15px",
                              color: "#14213d",
                            }}
                          >
                            {t(
                              "newOriginationSummary.Ticket Size by (NTB / ETB).pageTitle"
                            )}
                          </h1>
                          {drawdownAnalysisSummaryLoading ? (
                            <div
                              className="d-flex justify-content-center align-items-center"
                              style={{ minHeight: "50vh" }}
                            >
                              <Loader />
                            </div>
                          ) : drawdownAnalysisSummary.length > 0 ? (
                            <div style={{ overflowX: "auto" }}>
                              <table
                                className="table"
                                style={{ margin: "20px", minWidth: "600px" }}
                              >
                                <thead>
                                  <tr style={{ fontSize: 12, fontWeight: 300 }}>
                                    <th
                                      className="tableHead"
                                      style={{ minWidth: "130px" }}
                                      scope="col"
                                    >
                                      {t(
                                        "newOriginationSummary.Ticket Size by (NTB / ETB).Payment Type"
                                      )}
                                    </th>
                                    <th
                                      className="tableHead"
                                      style={{ minWidth: "130px" }}
                                      scope="col"
                                    >
                                      {t(
                                        "newOriginationSummary.Ticket Size by (NTB / ETB).Releation Type"
                                      )}
                                    </th>
                                    {drawdownAnalysisSummary.map((obj: any) => (
                                      <th
                                        className="tableHead"
                                        key={obj.period}
                                        scope="col"
                                      >
                                        {obj.period}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody
                                  style={{ fontSize: 13, fontWeight: 400 }}
                                >
                                  {["emi_loan_details", "ewi_loan_details"].map(
                                    (
                                      paymentType: string,
                                      typeIndex: number
                                    ) => {
                                      let lastPaymentType: string | null = null;
                                      return [
                                        "new_to_kpb",
                                        "existing_to_kpb",
                                        "past_kpb",
                                      ].map(
                                        (
                                          category: string,
                                          catIndex: number
                                        ) => {
                                          let lastCategory: string | null =
                                            null;
                                          return ["avg_loan_disb_amt"].map(
                                            (key: string, rowIndex: number) => {
                                              const showPaymentType =
                                                lastPaymentType !==
                                                keyLabelingData[paymentType];
                                              const showCategory =
                                                lastCategory !==
                                                keyLabelingData[category];
                                              lastPaymentType =
                                                keyLabelingData[paymentType];
                                              lastCategory =
                                                keyLabelingData[category];

                                              return (
                                                <tr
                                                  key={`${typeIndex}-${catIndex}-${rowIndex}`}
                                                >
                                                  {showPaymentType && (
                                                    <td
                                                      className="border text-center align-middle"
                                                      rowSpan={3}
                                                      style={{
                                                        minWidth: "120px",
                                                      }}
                                                      scope="col"
                                                    >
                                                      {
                                                        keyLabelingData[
                                                          paymentType
                                                        ]
                                                      }
                                                    </td>
                                                  )}
                                                  {showCategory && (
                                                    <td
                                                      className="border text-center align-middle"
                                                      rowSpan={1}
                                                      style={{
                                                        minWidth: "100px",
                                                        backgroundColor: [
                                                          "New-to-KPB",
                                                          "Past-KPB",
                                                        ].includes(
                                                          keyLabelingData[
                                                            category
                                                          ]
                                                        )
                                                          ? "#e9ecef"
                                                          : "white",
                                                      }}
                                                      scope="col"
                                                    >
                                                      {
                                                        keyLabelingData[
                                                          category
                                                        ]
                                                      }
                                                    </td>
                                                  )}
                                                  {drawdownAnalysisSummary.map(
                                                    (
                                                      obj: any,
                                                      colIndex: number
                                                    ) => (
                                                      <td
                                                        key={`${catIndex}-${rowIndex}-${colIndex}`}
                                                        style={{
                                                          minWidth: "100px",
                                                          backgroundColor: [
                                                            "New-to-KPB",
                                                            "Past-KPB",
                                                          ].includes(
                                                            keyLabelingData[
                                                              category
                                                            ]
                                                          )
                                                            ? "#e9ecef"
                                                            : "white",
                                                        }}
                                                      >
                                                        {obj?.[paymentType]?.[
                                                          category
                                                        ]?.[key] || "--"}
                                                      </td>
                                                    )
                                                  )}
                                                </tr>
                                              );
                                            }
                                          );
                                        }
                                      );
                                    }
                                  )}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <p>No data available for the selected criteria</p>
                          )}
                        </div>

                        {/* Chart Section */}
                        <div className="col-md-5 mt-5 p-3">
                          <h1
                            style={{
                              fontSize: 15,
                              fontWeight: 600,
                              fontFamily: "Arial",
                              fontStyle: "normal",
                              textAlign: "start",
                              paddingLeft: "15px",
                              color: "#14213d",
                            }}
                          >
                            {t("newOriginationSummary.title")}
                          </h1>
                          <BarChart
                            title=""
                            labels={
                              orginationSummary?.map(
                                (obj: any) => obj.period
                              ) || []
                            }
                            dataSets={[
                              {
                                label: t("newOriginationSummary.label1"),
                                data: (orginationSummary ?? []).map(
                                  (obj: any) => obj?.ticket_size_emi || 0
                                ),
                                backgroundColor: "#0077b6",
                              },
                              {
                                label: t("newOriginationSummary.label2"),
                                data: (orginationSummary ?? []).map(
                                  (obj: any) => obj?.ticket_size_ewi || 0
                                ),
                                backgroundColor: "#fca311",
                              },
                            ]}
                            xLabel={
                              periodMap[selectedFilters?.period[0]] || "Period"
                            }
                            yLabel={t("newOriginationSummary.yLabel")}
                          />
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OriginationSummary;
